import { create } from "zustand";
import {
  DevtoolsOptions,
  PersistOptions,
  createJSONStorage,
  devtools,
  persist,
} from "zustand/middleware";
import {
  createLoadingState,
  createAuthState,
  createCartState,
  createDataState,
  AppState,
  AppProps,
  createOrdersState,
  defaultBuildingsProps,
  createBuildingsState,
  defaultOrdersProps,
  defaultCartProps,
  defaultCafesProps,
  createCafesState,
  createPaymentsState,
  defaultPaymentsProps,
  createOrdersHistoryState,
  defaultOrdersHistoryProps,
  createSiteMessageState,
  defaultSiteMessageProps,
  createFeatureFlagsState,
  defaultFeatureFlagsProps,
} from "./states";
import { ApiClient } from "@dining/api-client";
import { appInsights } from "../appInsights";
import { AppType } from "@microsoft/metaos-appcontextutils";
import { BuildingCafeList, BuildingSortOption } from "../sharedTypes";
import dayjs from "dayjs";
import { createMenuListState, defaultMenuListProps } from "./states/menuList";

export type PersistedState = {
  buildings: {
    values: BuildingCafeList[];
    sortedBy: BuildingSortOption;
  };
};

export const persistOptions: PersistOptions<AppState, PersistedState> = {
  name: `${process.env.APP_ENV}-dining-store`,
  storage: createJSONStorage(() => sessionStorage),
  partialize: (state: AppState) => ({
    buildings: {
      values: state.buildings.values,
      sortedBy: state.buildings.sortedBy,
    },
  }),
  merge: (persistedState, initialState: AppState) =>
    ({
      ...initialState,
      buildings: {
        ...initialState.buildings,
        values: (persistedState as PersistedState).buildings.values,
        sortedBy: (persistedState as PersistedState).buildings.sortedBy,
      },
    }) as AppState,
};

const devtoolsOptions: DevtoolsOptions = {
  name: `${process.env.APP_ENV}-dining-store`,
  enabled: process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test",
};

export const defaultAppProps: AppProps = {
  appInsights,
  authenticated: false,
  context: {
    appType: AppType.standAlone,
  },
  ...defaultBuildingsProps,
  ...defaultCafesProps,
  selectedDate: dayjs(),
  loading: false,
  ...defaultCartProps,
  ...defaultOrdersProps,
  ...defaultPaymentsProps,
  ...defaultMenuListProps,
  ...defaultOrdersHistoryProps,
  ...defaultSiteMessageProps,
  ...defaultFeatureFlagsProps,
};

export type AppStore = ReturnType<typeof createAppStore>;

export const createAppStore = (initialState?: Partial<AppProps>) =>
  create<AppState>()(
    devtools(
      persist(
        (...a) => ({
          ...defaultAppProps,
          ...initialState,
          setClient: (client: ApiClient) => a[0]({ client }),
          ...createAuthState(...a),
          ...createPaymentsState(initialState)(...a),
          ...createBuildingsState(initialState)(...a),
          ...createCafesState(initialState)(...a),
          ...createCartState(initialState)(...a),
          ...createDataState(...a),
          ...createLoadingState(...a),
          ...createOrdersState(initialState)(...a),
          ...createMenuListState(initialState)(...a),
          ...createOrdersHistoryState(initialState)(...a),
          ...createSiteMessageState(initialState)(...a),
          ...createFeatureFlagsState(initialState)(...a),
        }),
        persistOptions,
      ),
      devtoolsOptions,
    ),
  );
